/**
 * API client for tank parameters endpoint.
 *
 * Provides access to the unified parameter mapping system which:
 * - Lists enabled parameters for a tank with their device sources
 * - Provides feature flags indicating what data types are available
 */
import { buildApiUrl } from './config';
import { apiClient } from './client';

/**
 * Information about a single parameter for a tank.
 */
export interface ParameterInfo {
  /** Unique parameter key, e.g., 'temp', 'ph', 'alkalinity' */
  key: string;
  /** Human-readable name, e.g., 'Temperature', 'pH', 'Alkalinity' */
  display_name: string;
  /** Unit of measurement, e.g., 'C', '', 'dKH' */
  unit: string;
  /** Parameter category: 'sensor', 'water_test', 'nutrient', 'trace' */
  category: string;
  /** Device type providing this parameter, e.g., 'apex_fusion', 'mastertronic' */
  device_type: string;
  /** Device configuration ID (optional) */
  device_config_id?: string;
  /** Whether parameter is enabled for display */
  is_enabled: boolean;
  /** Display order for sorting */
  display_order: number;
}

/**
 * Feature flags indicating what data types are available for a tank.
 */
export interface TankFeatures {
  /** Has Apex Fusion controller configured */
  has_apex: boolean;
  /** Has Mastertronic water tester configured */
  has_mastertronic: boolean;
  /** Has Alkatronic configured */
  has_alkatronic: boolean;
  /** Has AquaWiz configured */
  has_aquawiz: boolean;
  /** Has Dosetronic configured */
  has_dosetronic: boolean;
  /** Has Aquarimate configured */
  has_aquarimate: boolean;
  /** Has maintenance tracking enabled (via Aquarimate) */
  has_maintenance: boolean;
  /** Has ICP test data */
  has_icp: boolean;
  /** Has power monitoring data (Apex EnergyBar) */
  has_power_monitoring: boolean;
  /** Has any Kamoer device configured */
  has_kamoer: boolean;
  /** Can use Dosing: doser hardware, manual dosing events, or an active schedule */
  has_doser: boolean;
}

/**
 * Response from the tank parameters endpoint.
 */
export interface TankParametersResponse {
  /** List of enabled parameters for the tank */
  parameters: ParameterInfo[];
  /** Feature flags for conditional UI rendering */
  features: TankFeatures;
}

/**
 * Fetch parameters and feature flags for a specific tank.
 *
 * @param tankId - The tank ID to fetch parameters for
 * @returns Tank parameters and feature flags
 */
export async function getTankParameters(tankId: string): Promise<TankParametersResponse> {
  const url = buildApiUrl(`/api/tanks/${tankId}/parameters`);

  const response = await apiClient(url, {
    method: 'GET',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

/**
 * React Query key for tank parameters.
 * Use this for cache invalidation and query key consistency.
 */
export const tankParametersQueryKey = (tankId: string | undefined) => ['tank-parameters', tankId];
